// Import package components
import { useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import UIkit from "uikit";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AppContexts } from "../../../providers";
import SMSEmailInputBox from "../../common/sms-email-inputbox";
import {
  MessageLengthEditorStatus,
  MessageLengthInlineStatus,
} from "../../common/SmsMessageLengthStatus";
import { analyzeSmsMessage } from "../../../utils/smsMessageLength";
import { buildGuestSendRouteState } from "../../messages/guest-send-state";

const schema = yup.object().shape({
  body: yup.string().required(),
});

const defaultValues = {
  method: "sms",
  body: "",
  subject: "",
};

const GuestMessages = () => {
  const [loading, setLoading] = useState(false);

  const {
    register,
    setValue,
    getValues,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "all",
    resolver: yupResolver(schema),
  });
  const reservationContext = useContext(AppContexts.ReservationsContext);
  const inboxContext = useContext(AppContexts.InboxContext);
  const authContext = useContext(AppContexts.AuthContext);
  const { genUUID } = useContext(AppContexts.UtilContext);
  const { get, settings } = useContext(AppContexts.SettingsContext);
  const history = useHistory();

  useEffect(() => {
    Object.keys(defaultValues).forEach((formname) => {
      setValue(formname, defaultValues[formname]);
    });
    get(["branding"]);
  }, [reservationContext.reservation]);

  useEffect(() => {
    if (settings && settings.branding) {
      setValue("from_name", settings.branding?.from_name);
      setValue("from_email", settings.branding?.from_email);
    }
  }, [settings]);

  const handleSendMsg = async (data) => {
    setLoading(true);
    console.log(reservationContext.reservation);
    if (!reservationContext.reservation.formatted_phone) {
      setLoading(false);
      UIkit.notification("Phone number is missing", "warning");
      return;
    }
    const clientMessageId = genUUID();
    const createdAt = new Date().toISOString();
    let sms = {
      guest_reservation_id: reservationContext.reservation.guest_reservation_id,
      message: data.body,
      phone: reservationContext.reservation.phone,
      client_message_id: clientMessageId,
    };
    try {
      const result = await reservationContext.createSMS(sms);
      if (!result?.thread_id) {
        throw new Error("The message response did not include a thread.");
      }

      const currentThreadId =
        inboxContext.threadDetail?.id ?? inboxContext.threadDetail?.thread_id;
      const knownMessageIds =
        String(currentThreadId) === String(result.thread_id)
          ? (inboxContext.threadDetail?.messages || [])
              .map((message) => message?.message_id)
              .filter(
                (messageId) =>
                  messageId !== null && messageId !== undefined,
              )
          : [];
      const routeState = buildGuestSendRouteState({
        reservation: reservationContext.reservation,
        threadId: result.thread_id,
        message: data.body,
        clientMessageId,
        senderId:
          authContext.user?.user_id ?? authContext.user?.user?.user_id ?? null,
        createdAt,
        knownMessageIds,
      });

      inboxContext.setSelectedThreadId(result.thread_id);
      inboxContext.upsertInboxThread(routeState.activeThread);
      setLoading(false);
      UIkit.modal("#modal-guest-messages").hide();
      UIkit.notification("Text message sent successfully.", "success");
      setValue("body", "");
      history.push(`/messages/${result.thread_id}`, routeState);
    } catch (error) {
      console.warn(error);
      setLoading(false);
      UIkit.notification(
        error?.response?.data?.error ||
          "Unable to send the text message. Please try again.",
        "danger",
      );
    }
  };

  const handleSelectedResponse = (response) => {
    setValue("body", response);
  };

  const method = useMemo(() => getValues("method"), [watch("method")]);
  const body = useMemo(() => getValues("body"), [watch("body")]);
  const messageLength = useMemo(() => {
    const reservation = reservationContext.reservation || {};

    return analyzeSmsMessage(body || "", {
      CompanyName: settings.branding?.company_name,
      FirstName: reservation.first_name,
      LastName: reservation.last_name,
      Email: reservation.email,
      PhoneNumber: reservation.formatted_phone || reservation.phone,
      PropertyId: reservation.pms_id,
      PropertyAddress: reservation.rental_address,
      CheckIn: reservation.check_in,
      CheckOut: reservation.check_out,
      ReservationId: reservation.reservation_id || reservation.native_id,
      DoorCode: reservation.door_code,
    });
  }, [body, reservationContext.reservation, settings.branding?.company_name]);

  const handleClose = useCallback(() => {
    Object.keys(defaultValues).forEach((formname) => {
      setValue(formname, defaultValues[formname]);
    });
  }, []);

  return (
    <div
      id="modal-guest-messages"
      className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close="true"
          ></button>
          <div className="uk-modal-header">
            <h2 className="uk-modal-title">Send a Message</h2>
          </div>
          <div className="uk-modal-body">
            <div className="add-contact-form">
              <form
                className="uk-grid-small"
                uk-grid="true"
                onSubmit={handleSubmit(handleSendMsg)}
              >
                {method === "email" && (
                  <>
                    <div className="uk-width-1-1">
                      <div className="uk-width-1-2@s">
                        <label className="uk-form-label" htmlFor="txt-subject">
                          Subject
                        </label>
                        <div className="uk-form-controls">
                          <input
                            className="uk-input"
                            id="txt-subject"
                            type="text"
                            {...register("subject")}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="uk-width-1-1">
                      <div className="uk-width-1-2@s">
                        <label className="uk-form-label" htmlFor="txt-name">
                          From Name
                        </label>
                        <div className="uk-form-controls">
                          <input
                            className="uk-input"
                            id="txt-name"
                            type="text"
                            {...register("from_name")}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="uk-width-1-1">
                      <div className="uk-width-1-2@s">
                        <label className="uk-form-label" htmlFor="txt-email">
                          From Email Address
                        </label>
                        <div className="uk-form-controls">
                          <input
                            className="uk-input"
                            id="txt-email"
                            type="text"
                            {...register("from_email")}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
                <div className="uk-width-1-1">
                  <SMSEmailInputBox
                    value={body || ""}
                    limit={method === "sms"}
                    macrosOrResponse={false}
                    onChange={(event) => setValue("body", event.target.value)}
                    onSavedReply={handleSelectedResponse}
                    optimizeWithAI={true}
                    optimizationSource="guests"
                    optimizationTrigger="icon"
                    normalizeOnPaste={method === "sms"}
                    messageLengthStatus={
                      method === "sms" ? (
                        <MessageLengthInlineStatus analysis={messageLength} />
                      ) : null
                    }
                  />
                  {method === "sms" && (
                    <MessageLengthEditorStatus analysis={messageLength} />
                  )}
                </div>
                <div className="uk-width-1-1 uk-margin-top uk-flex">
                  <button
                    className="uk-button hms-btn hms-red-btn"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? "Sending..." : "Send"}
                  </button>
                  <button
                    className="uk-button hms-btn uk-offcanvas-close"
                    onClick={handleClose}
                    type="button"
                  >
                    Cancel
                  </button>

                  {!!reservationContext.reservation.thread_id &&
                    !!reservationContext.reservation.formatted_phone && (
                      <button
                        className="uk-button hms-btn hms-orange-btn uk-margin-small-bottom"
                        onClick={() =>
                          history.push(
                            `/messages/${reservationContext.reservation.thread_id}`,
                          )
                        }
                        type="button"
                      >
                        View Conversation
                      </button>
                    )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestMessages;
